import {
  Card,
  CardBody,
  ContextualBanner,
  Input,
  Toggle,
  Tooltip,
} from '@hawkins/components';
import { useState, useEffect, useMemo } from 'react';
import { ceiling } from 'src/domains/calculators/utilities/ceiling';
import { roundToNearestEven } from 'src/domains/calculators/utilities/round';
import { HorizontalInputStack } from './HorizontalInputStack';
import styles from './CustomFramingDecisionInput.module.css';
import { ResolutionInput } from '../FramingChart/types';
import { getCustomFramingDecisionValidation } from '../utilities/getCustomFramingDecisionValidation';
import { Resolution } from 'src/domains/calculators/utilities/types';

type CustomFramingDecisionInputProps = {
  customFramingDecision: ResolutionInput;
  lensSqueezeFactor: number;
  lockedAspectRatio?: number;
  onAspectRatioToggle: (locked: boolean) => void;
  setCustomFramingDecision: (resolution: ResolutionInput) => void;
  setLockAspectRatio: (locked: boolean) => void;
  canvasResolution?: Resolution | null;
  effectiveCanvas?: Resolution | null;
};

export const CustomFramingDecisionInput = ({
  customFramingDecision,
  lensSqueezeFactor,
  lockedAspectRatio,
  onAspectRatioToggle,
  setCustomFramingDecision,
  setLockAspectRatio,
  canvasResolution,
  effectiveCanvas,
}: CustomFramingDecisionInputProps) => {
  const customWidth = customFramingDecision.width;
  const customHeight = customFramingDecision.height;

  const hasCustomFramingDecision = !!customWidth && !!customHeight;

  const [showCustomFramingDecisionInputs, setShowCustomFramingDecisionInputs] =
    useState<boolean>(hasCustomFramingDecision);

  const [inputWidth, setInputWidth] = useState(customWidth?.toString() ?? '');
  const [inputHeight, setInputHeight] = useState(
    customHeight?.toString() ?? ''
  );

  const containerResolution = effectiveCanvas ?? canvasResolution;
  const containerName = effectiveCanvas ? 'Effective Canvas' : 'Canvas';

  const validation = useMemo(
    () =>
      getCustomFramingDecisionValidation(
        inputWidth,
        inputHeight,
        containerResolution,
        containerName
      ),
    [inputWidth, inputHeight, containerResolution, containerName]
  );

  useEffect(() => {
    setInputWidth(customWidth?.toString() ?? '');
  }, [customWidth]);

  useEffect(() => {
    setInputHeight(customHeight?.toString() ?? '');
  }, [customHeight]);

  const calculateWidth = (
    height: number,
    aspectRatio: number,
    lensSqueezeFactor: number
  ) => roundToNearestEven((height * aspectRatio) / lensSqueezeFactor);

  const calculateHeight = (
    width: number,
    aspectRatio: number,
    lensSqueezeFactor: number
  ) => roundToNearestEven((width * lensSqueezeFactor) / aspectRatio);

  const roundingMessage = 'Value is rounded up to the nearest even number';

  return (
    <>
      <Toggle
        aria-label="Toggle Custom Framing Decision Input"
        checked={showCustomFramingDecisionInputs}
        label="Custom Framing Decision"
        onChange={(_event, checked) => {
          setShowCustomFramingDecisionInputs(checked);
          setCustomFramingDecision({ width: null, height: null });
          setLockAspectRatio(false);
          setInputWidth('');
          setInputHeight('');
        }}
      />

      {showCustomFramingDecisionInputs && (
        <>
          {lockedAspectRatio && (
            <ContextualBanner
              compact
              text={`Values are constrained to ${lockedAspectRatio}:1 aspect ratio. Changing one dimension will automatically update the other.`}
              variant="warning"
            />
          )}
          <Card>
            <CardBody className={styles.customFramingDecisionInputContainer}>
              <HorizontalInputStack>
                <Tooltip label={roundingMessage} placement="bottom">
                  <Input
                    validationStatus={
                      validation.widthWarning ? 'error' : undefined
                    }
                    validationMessage={validation.widthWarning}
                    label="Width"
                    type="number"
                    fullWidth
                    value={inputWidth}
                    onChange={(event) => {
                      setInputWidth(event.target.value);
                    }}
                    onBlur={() => {
                      const rawWidth = Number(inputWidth);
                      if (rawWidth > 0) {
                        const roundedWidth = ceiling(rawWidth, 2);
                        setInputWidth(roundedWidth.toString());
                        if (lockedAspectRatio) {
                          const height = calculateHeight(
                            roundedWidth,
                            lockedAspectRatio,
                            lensSqueezeFactor
                          );
                          setCustomFramingDecision({
                            width: roundedWidth,
                            height,
                          });
                          setInputHeight(height.toString());
                        } else {
                          setCustomFramingDecision({
                            width: roundedWidth,
                            height: customHeight, // keep height unchanged
                          });
                        }
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip label={roundingMessage} placement="bottom">
                  <Input
                    validationStatus={
                      validation.heightWarning ? 'error' : undefined
                    }
                    validationMessage={validation.heightWarning}
                    label="Height"
                    type="number"
                    fullWidth
                    value={inputHeight}
                    onChange={(event) => {
                      setInputHeight(event.target.value);
                    }}
                    onBlur={() => {
                      const rawHeight = Number(inputHeight);
                      if (rawHeight > 0) {
                        const roundedHeight = ceiling(rawHeight, 2);
                        setInputHeight(roundedHeight.toString());
                        if (lockedAspectRatio) {
                          const width = calculateWidth(
                            roundedHeight,
                            lockedAspectRatio,
                            lensSqueezeFactor
                          );
                          setCustomFramingDecision({
                            width,
                            height: roundedHeight,
                          });
                          setInputWidth(width.toString());
                        } else {
                          setCustomFramingDecision({
                            height: roundedHeight,
                            width: customWidth, // keep width unchanged
                          });
                        }
                      }
                    }}
                  />
                </Tooltip>
              </HorizontalInputStack>
              {onAspectRatioToggle && (
                <div className={styles.lockAspectRatioToggle}>
                  <Toggle
                    label="Lock Aspect Ratio"
                    checked={!!lockedAspectRatio}
                    onChange={(_event, checked) => {
                      onAspectRatioToggle(checked);
                    }}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
};
